import { AlphaCard } from 'components/Alpha/cards/cardTypes';
import { IAlphaPendingConfirmationDto } from 'services/alpha/types';
import { IAlphaMessage, IAlphaPendingConfirmation } from 'store/alpha/types';

const CARD_KINDS: unknown[] = ['confirm', 'form', 'ask'];

const parseJson = (json: unknown): unknown => {
  if (typeof json !== 'string') return {};
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const toAlphaCard = (raw: unknown): AlphaCard | undefined => {
  const card = typeof raw === 'string' ? parseJson(raw) : raw;
  return isRecord(card) && CARD_KINDS.includes(card.kind)
    ? (card as unknown as AlphaCard)
    : undefined;
};

const hasToken = (dto: IAlphaPendingConfirmationDto): boolean =>
  typeof dto?.token === 'string' && dto.token.length > 0;

const isLive = (dto: IAlphaPendingConfirmationDto, now: number): boolean =>
  hasToken(dto) && Date.parse(dto.expiresAt) > now;

const asList = (
  dtos: IAlphaPendingConfirmationDto[] | undefined,
): IAlphaPendingConfirmationDto[] => (Array.isArray(dtos) ? dtos : []);

export const expiredPendingTokens = (
  dtos: IAlphaPendingConfirmationDto[] | undefined,
  now: number = Date.now(),
): string[] =>
  asList(dtos)
    .filter(dto => hasToken(dto) && !isLive(dto, now))
    .map(dto => dto.token);

export const toPendingConfirmations = (
  dtos: IAlphaPendingConfirmationDto[] | undefined,
  now: number = Date.now(),
): IAlphaPendingConfirmation[] =>
  asList(dtos)
    .filter(dto => isLive(dto, now))
    .map(dto => {
      const card = toAlphaCard(dto.card);
      return {
        toolName: dto.toolName,
        preview: parseJson(dto.previewJson),
        token: dto.token,
        ...(dto.title ? { title: dto.title } : {}),
        ...(CARD_KINDS.includes(dto.kind) ? { kind: dto.kind } : {}),
        ...(card ? { card } : {}),
        ...(typeof dto.reviewUrl === 'string' && dto.reviewUrl.length > 0
          ? { reviewUrl: dto.reviewUrl }
          : {}),
      };
    });

export type PendingCardView = 'form' | 'ask' | 'editable' | 'confirm';

export const pendingCardView = (pending: IAlphaPendingConfirmation): PendingCardView => {
  const { card } = pending;
  if (!card) return 'confirm';
  const kind = pending.kind ?? card.kind;
  if (kind === 'form') return 'form';
  if (kind === 'ask') return 'ask';
  return (card.editableFields?.length ?? 0) > 0 ? 'editable' : 'confirm';
};

export const clearBackedExpiryNotices = (
  messages: IAlphaMessage[],
  pending: IAlphaPendingConfirmation[],
): IAlphaMessage[] => {
  let index = messages.length - 1;
  while (index >= 0 && messages[index].localNotice) index -= 1;
  const marked = messages[index];
  if (!marked || marked.role !== 'assistant' || !marked.expiredConfirmationToolName) {
    return messages;
  }
  if (!pending.some(p => p.toolName === marked.expiredConfirmationToolName)) return messages;
  return messages.map((message, position) =>
    position === index
      ? { ...message, expiredConfirmationToolName: undefined, expiredConfirmationTitle: undefined }
      : message,
  );
};
